import { useCallback, useState } from "react";

import { useFormik } from "formik";
import i18next from "i18next";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import * as Yup from "yup";

import { login } from "../../../protocols";
import palette from "../../../theme/palette";

import { useEventListener } from "../../../hooks";

import { DotsLoadingComponent, InputComponent } from "../../ui";

import { useAuth } from "../../../context/auth";

import {
	BackButton,
	BackButtonBlock,
	BackButtonLabel,
	ButtonBlock,
	CardWrapper,
	FormSubtitle,
	FormTitle,
	ResetButton,
	ResetButtonBlock,
	ResetButtonLabel,
	SubmitButton,
	SubmitButtonLoadingCover,
	Wrapper
} from "../RegistrationForm/style";
import { getSafeRedirect } from "../../support/utils";

const ErrorMessage = styled.p`
  color: ${palette.error || "#d32f2f"};
  font-size: 13px;
  margin: -8px 0 8px;
  text-align: center;
`;

const LoginForm = () => {
  const { language, t } = i18next;
  const { getUserData } = useAuth();
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);

  const validationSchema = Yup.object().shape({
    login: Yup.string().trim().email(t("enter_valid_mail")).required(t("required")),
    password: Yup.string().min(6, t("must_field_characters")).required(t("required")),
});


  const handleFormSubmit = async (values) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setApiError(null);

    try {
      const loginResult = await login({ ...values, language });

      if (loginResult?.data?.error_code) {
        const { error_code, error_desc } = loginResult.data;
        if (error_code === 9 || error_code === 11) {
          setApiError(t("incorrect_client_login"));
        } else {
          setApiError(error_desc || t("incorrect_client_login"));
        }
      } else {
        localStorage.setItem("token", loginResult?.data?.token);
        await getUserData(loginResult?.data);
        navigate(getSafeRedirect(new URLSearchParams(window.location.search).get("redirect")) || "/");
      }
    } catch (error) {
      console.log({ error });
      const errorCode = error?.response?.data?.error_code;
      const errorMessage = error?.response?.data?.error_desc || error?.response?.data?.message || error?.message;
      if (errorCode === 9 || errorCode === 11) {
        setApiError(t("incorrect_client_login"));
      } else {
        setApiError(errorMessage || t("incorrect_client_login"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const { values, errors, touched, handleChange, handleBlur, handleSubmit } = useFormik(
    {
      initialValues: {
        login: "",
        password: "",
      },
      validationSchema,
      onSubmit: handleFormSubmit,
      validateOnBlur: true,
    }
);

  const handleKeyPress = useCallback(e => {
    if (e.key === "Enter" || e.keyCode === 13) {
      handleSubmit();
    }
  }, [handleSubmit]);

  useEventListener("keypress", handleKeyPress);

  return (
    <Wrapper>
			<CardWrapper>
				<FormTitle>
					{t("welcome_back")}
				</FormTitle>

				<FormSubtitle>
					{t("login_as_user")}
				</FormSubtitle>

				<InputComponent
					value={values.login}
					onChange={handleChange("login")}
					type="email"
					placeholder={t("enter_email")}
					label={t("email")}
					error={errors.login}
					onBlur={handleBlur("login")}
					touched={touched.login}
					required
				/>

				<InputComponent
					value={values.password}
					onChange={handleChange("password")}
					type="password"
					placeholder={t("enter_password")}
					label={t("password_text")}
					error={errors.password}
					onBlur={handleBlur("password")}
					touched={touched.password}
					required
				/>

				{apiError && <ErrorMessage>{apiError}</ErrorMessage>}

				<ResetButtonBlock>
					<ResetButtonLabel>
						{t("forgot_password_text")}
					</ResetButtonLabel>
					<ResetButton>
						<Link to="/forgot-password">
							{t("reset_password_text")}
						</Link>
					</ResetButton>
				</ResetButtonBlock>

				<ButtonBlock>
					<SubmitButton
						onClick={handleSubmit}
						type="button"
					>
						{t("enter_text")}
						{isSubmitting && (
							<SubmitButtonLoadingCover>
								<DotsLoadingComponent color={palette.white} />
							</SubmitButtonLoadingCover>
						)}
					</SubmitButton>
				</ButtonBlock>

				<BackButtonBlock>
					<BackButtonLabel>
						{t("new_customer")}
					</BackButtonLabel>
					<BackButton>
						<Link to="/registration">
							{t("registration_text")}
						</Link>
					</BackButton>
				</BackButtonBlock>
			</CardWrapper>
    </Wrapper>
  )
};

export default LoginForm;
